import { api_configuration } from "../../common";
import api_app = api_configuration.libraries.gui_definition_lib.libraries.model_lib.types.application.alan_api;
import api_cs = api_configuration.types.client_state.alan_api;
import api_client_request = api_configuration.libraries.gui_definition_lib.libraries.model_lib.types.client_request.alan_api;
import { MutableVariable, createVariable, ScalarProvider, WeakCache } from "../../../../../stream";
import { Pending } from "../Status";
import { Sink, Source, None } from "../Symbols";
import { iText } from "../state/Text";
import { iReference } from "../state/Reference";
import { iStategroup, iState } from "../state/Stategroup";
import * as Entry from "../state/Entry";
import * as Collection from "../state/Collection";
import { registerObjectPathTail, registerText, registerStategroup } from "../state/registerClientState";
import DestructorTree from "../DestructorTree";
import { Failed } from "../Status";
import { createNodePathFromObjectPathTail } from "../navigation/NodePathBuilder";
import { createIdPathBuilderFromNode } from "../navigation/createIdPathFromNode";
import { DatastoreProxy } from "../interface/DatastoreProxy";
import { CollectionPathBuilder } from "../navigation/createCollectionPathBuilder";
import { createNodeExistanceFilter } from "../state 2 interface/createPropertiesDictionaryFromColumns";
import NodePathBuilder from "../navigation/NodePathBuilder";
import * as queryQueue from "../queryQueue";
import { iCollection } from "../state/Collection";
import ClientStateObjectProxy from "./ClientStateObjectProxy";

type Entry = api_cs.Centries__collection;

export function* ordered_graphs(collection: api_cs.Ccollection): IterableIterator<iGraph> {
	switch (collection.properties.has_ordered_graphs.state.name) {
		case "yes":
			for (const graph of collection.properties.has_ordered_graphs.state.node.properties.ordered_graphs.values()) {
				yield iGraph(graph);
			}
			break;
		case "no":
	}
}

export function definesGraphInsertionPosition(stategroup: api_cs.Cstate_group__type__property) {
	const entity = stategroup.component_root.input.entity().variant;
	switch (entity.name) {
		case "entry":
			return [...ordered_graphs(entity.definition.parent)].reduce((res, graph) => {
				if (res) return res;
				if (graph.ordering_stategroup === stategroup.inferences.state_group_type()) {
					return graph.edge_reference.properties.ui.properties.default_value.switch({
						no: () => true,
						yes: () => false,
					});
				}
				return false;
			}, false);
		case "root":
	}
	return false;
}

function getBackendKeyValue(entry: Entry): string {
	const backend_known = entry.properties.key.ref.properties.backend_known.state;
	switch (backend_known.name) {
		case "no":
			return entry.properties.key.ref.properties.is_set.cast("yes").properties.text;
		case "yes":
			return backend_known.node.properties.value;
	}
}

class ForwardProxy {
	constructor(
		readonly graph: Graph,
		readonly definer: Entry | Graph | undefined,
		public backward: Node | Graph | BackwardProxy,
		public key: string | Sink | Pending
	) {
		graph.fproxies.add(this);
	}

	edgeUpdateValue(): string | Sink | undefined {
		if (this.key === Pending) return undefined;
		return this.key;
	}

	destruct() {
		this.graph.fproxies.delete(this);
	}

	get destroyed() {
		return !this.graph.fproxies.has(this);
	}
}

class BackwardProxy {
	destructor: DestructorTree | undefined;

	constructor(
		readonly graph: Graph,
		readonly definer: Entry | Graph | undefined,
		public forward: Node | Graph | ForwardProxy,
		public edge: string | Sink
	) {
		graph.bproxies.add(this);
	}

	updateEdge() {
		if (!!this.destructor && !this.destructor.destroyed) return;
		this.destructor = new DestructorTree();

		queryQueue.add(
			this.graph.queryVertex(this.edge).then((key) => {
				if (this.destructor!.destroyed) return;
				if (key === Failed) return;
				if (key === None) return;
				const exp = Collection.openEntry(this.graph.collection, key, this.destructor!)();
				const sub = exp.subscribe({ update: () => undefined });
				this.destructor!.onDestroy(() => exp.unsubscribe(sub));
			})
		);
	}

	isModified() {
		return this.forward !== this.definer;
	}

	destruct() {
		this.destructor?.destroy();
		this.graph.bproxies.delete(this);
	}

	get destroyed() {
		return !this.graph.bproxies.has(this);
	}
}

class Node {
	backward: Node | Graph | BackwardProxy;
	forward: Node | Graph | ForwardProxy;

	constructor(
		readonly graph: Graph,
		readonly entry: Entry,
		public destructor: DestructorTree,
		readonly sink_state: api_cs.Cstates__state_group__type__property,
		readonly edge_state: api_cs.Cstates__state_group__type__property,
		readonly edge_reference: api_cs.Cyes__navigable
	) {}

	updateEdge(by: "user" | "engine") {
		if (this.forward instanceof Node) {
			iState(this.edge_state).__select(by);
			iReference(this.edge_reference).setReferencedEntry(this.forward.entry);
		}
		const update_value = this.forward.edgeUpdateValue();
		if (update_value !== undefined) {
			if (update_value === Sink) {
				iState(this.sink_state).__select(by);
			} else {
				iState(this.edge_state).__select(by);
				iText(this.edge_reference.parent).__update(update_value, by);
			}
		}
		if (Entry.isMarkedForDeletion(this.entry)) {
			this.backward.updateEdge(by);
		}
	}

	edgeModified(): boolean {
		return this.sink_state.parent.properties.is_modified.switch({
			no: () =>
				this.edge_state.properties.is_selected.switch({
					no: () => false,
					yes: () => iText(this.edge_reference.parent).modified.value,
				}),
			yes: () => true,
		});
	}

	isModified(): boolean {
		return Entry.isMarkedForDeletion(this.entry) || this.edgeModified();
	}

	getEdgeValue() {
		return this.sink_state.properties.is_selected.switch({
			yes: () => Sink as Sink | string | undefined,
			no: () =>
				this.edge_reference.parent.properties.is_set.switch({
					no: () => undefined,
					yes: ($) => $.properties.text,
				}),
		});
	}

	getServerEdgeValue() {
		return this.sink_state.parent.properties.initialized.switch({
			no: () => undefined,
			yes: ($) => {
				const backend_state = $.properties.state_in_backend.ref;
				if (backend_state === this.sink_state) return Sink;
				if (backend_state === this.edge_state) {
					return this.edge_reference.parent.properties.backend_known.switch({
						no: () => undefined,
						yes: ($) => $.properties.value,
					});
				}
				return undefined; // What to do in this case?
			},
		});
	}

	discardEdge() {
		this.sink_state.parent.properties.initialized.switch({
			no: () => iStategroup(this.sink_state.parent).discard(),
			yes: ($) => {
				const backend_state = $.properties.state_in_backend.ref;
				iState(backend_state).__select("engine");
				if (backend_state === this.edge_state) {
					this.edge_reference.parent.properties.backend_known.switch({
						no: () => iText(this.edge_reference.parent).discard(),
						yes: ($) => iText(this.edge_reference.parent).__update($.properties.value, "engine"),
					});
				}
			},
		});
	}

	edgeUpdateValue(): string | Sink | undefined {
		if (Entry.isMarkedForDeletion(this.entry)) {
			return this.forward.edgeUpdateValue();
		} else {
			return this.entry.properties.key.ref.properties.is_set.switch({
				no: () => undefined,
				yes: ($) => $.properties.text,
			});
		}
	}

	get key() {
		return this.entry.properties.key.ref.properties.is_set.switch({
			no: () => "-",
			yes: ($) => $.properties.text,
		});
	}

	createBackwardProxy() {
		return new BackwardProxy(this.graph, this.entry, this, getBackendKeyValue(this.entry));
	}

	createForwardProxy() {
		return new ForwardProxy(this.graph, this.entry, this, this.getServerEdgeValue() ?? Pending);
	}

	destruct() {
		this.destructor.destroy();
		this.graph.nodes.delete(this.entry);
	}

	get destroyed() {
		return this.destructor.destroyed || this.entry.destroyed;
	}
}

class Graph extends ClientStateObjectProxy {
	readonly ordering_stategroup_path: api_app.Cobject_path_tail;
	readonly ordering_stategroup: api_app.Cstate_group;
	readonly sink_state: api_app.Cstates__state_group;
	readonly edge_state: api_app.Cstates__state_group;
	readonly edge_reference: api_app.Ctext__type__property;

	readonly collection: api_cs.Ccollection;

	nodes = new Map<Entry, Node>();
	fproxies = new Set<ForwardProxy>();
	bproxies = new Set<BackwardProxy>();
	pending_updates = new Set<Node>();

	forward: Node | Graph | ForwardProxy; // Source
	backward: Node | Graph | BackwardProxy; // Sink

	default: Sink | Source;
	source_key: string | Sink | Pending = Pending;
	readonly key = Sink;

	constructor(readonly ordered_graph: api_cs.Cordered_graphs) {
		super();
		this.collection = ordered_graph.parent.parent;
		switch (this.collection.component_root.properties.location.state.name) {
			case "local":
				this.forward = this;
				this.backward = this;
				break;
			case "remote":
				this.forward = this.createForwardProxy();
				this.backward = this.createBackwardProxy();
				break;
		}
		const definition = ordered_graph.key.ref.properties.ordering_property_path;
		this.ordering_stategroup_path = definition.properties.ordering_state_group_path.properties.head;
		this.ordering_stategroup = definition.properties.ordering_state_group_path.inferences.state_group();
		this.sink_state = definition.properties.ordering_states.properties.sink_state.ref;
		this.edge_state = definition.properties.ordering_states.properties.edge_state.ref;
		this.edge_reference = definition.properties.ordering_states.properties.edge_reference.ref.parent;
		// Always default to Source, except when the default expression only and explicitly references the Sink
		this.default = this.ordering_stategroup.properties.ui.properties.default_state.switch({
			no: () => Source as Source | Sink,
			yes: ($) => {
				const operation = $.properties.default_.properties.expression.properties.operation.state;
				if (operation.name !== "produce value") return Source;
				const value = operation.node.properties.value.state;
				if (value.name !== "scalar") return Source;
				const expression = value.node.properties.expression.properties.type.state;
				if (expression.name !== "state") return Source;
				return expression.node.properties.state.ref.switch({
					state: ($) => ($ === this.sink_state ? Sink : Source),
				});
			},
		});
	}

	get destroyed() {
		return this.ordered_graph.destroyed;
	}

	createBackwardProxy() {
		return new BackwardProxy(this, this, this, Sink);
	}

	getBackwardProxy(definer: Entry | Graph): BackwardProxy | undefined {
		for (const proxy of this.bproxies) {
			if (proxy.definer === definer) return proxy;
		}
		return undefined;
	}

	createForwardProxy() {
		return new ForwardProxy(this, this, this, this.source_key);
	}

	getForwardProxy(definer: Entry | Graph): ForwardProxy | undefined {
		for (const proxy of this.fproxies) {
			if (proxy.definer === definer) return proxy;
		}
		return undefined;
	}

	createNode(entry: Entry, backward?: Node | Graph | BackwardProxy, forward?: Node | Graph | ForwardProxy) {
		const destructor = new DestructorTree();
		const context_node = registerObjectPathTail(this.ordering_stategroup_path, entry.properties.node, destructor)!;
		const ordering_stategroup = registerStategroup(this.ordering_stategroup, context_node, destructor);
		const edge_state = ordering_stategroup.properties.states.get(this.edge_state.key)!;
		const node = new Node(
			this,
			entry,
			destructor,
			ordering_stategroup.properties.states.get(this.sink_state.key)!,
			edge_state,
			registerText(this.edge_reference, edge_state.properties.node, destructor).properties.navigable.cast("yes")
		);
		node.backward = backward ?? node.createBackwardProxy();
		node.forward = forward ?? node.createForwardProxy();
		this.nodes.set(entry, node);
		return node;
	}

	getNode(entry: Entry) {
		return this.nodes.get(entry);
	}

	findByKey(key: string | Sink): Node | Graph | undefined {
		if (key === Sink) return this;
		for (const node of this.nodes.values()) {
			if (
				node.entry.properties.node.properties.is_busy_saving.switch({
					no: () =>
						node.entry.properties.key.ref.properties.backend_known.switch({
							no: () => false,
							yes: ($) => $.properties.value === key,
						}),
					yes: () =>
						node.entry.properties.key.ref.properties.is_set.switch({
							no: () => false,
							yes: ($) => $.properties.text === key,
						}),
				})
			)
				return node;
		}
		return undefined;
	}

	updateEdge(): void {
		return void 0;
	}

	isModified() {
		return this.source_key !== this.forward.key;
	}

	edgeUpdateValue(): string | Sink | undefined {
		return Sink;
	}

	connect(
		backward: Node | Graph | BackwardProxy,
		forward: Node | Graph | ForwardProxy,
		by: "user" | "engine" = "engine"
	): void {
		if (backward.destroyed || forward.destroyed) throw new Error("Something unexpected");
		forward.backward = backward;
		backward.forward = forward;
		backward.updateEdge(by);
	}

	move(node: Node | undefined, before: Node | Graph | ForwardProxy | undefined, by: "user" | "engine" = "user") {
		if (!node || !before) return;
		if (node === before) return;
		this.connect(node.backward, node.forward, by);
		this.connect(before.backward, node, by);
		this.connect(node, before, by);
		this.updateSorting();
	}

	merge(bproxy: BackwardProxy, fproxy: ForwardProxy) {
		const forward = bproxy.forward;
		const backward = fproxy.backward;
		backward.forward = forward;
		forward.backward = backward;
		fproxy.destruct();
		bproxy.destruct();
		this.updateSorting();
	}

	private _reset_as_local() {
		for (const entry of this.collection.properties.entries.values()) {
			this.addEntry(entry);
		}
		this.updateSorting();
	}
	private _clear() {
		for (const node of this.nodes.values()) {
			node.destruct();
		}
		for (const proxy of this.fproxies.values()) {
			proxy.destruct();
		}
		for (const proxy of this.bproxies.values()) {
			proxy.destruct();
		}
	}
	reset(force_init = true) {
		for (const node of this.nodes.values()) {
			switch (node.entry.properties.location.state.name) {
				case "local":
					break;
				case "remote":
					node.entry.properties.location.state.node.properties.marked_for_deletion.ensure("no", {});
					node.discardEdge();
			}
			node.destruct();
		}
		for (const proxy of this.fproxies.values()) {
			proxy.destruct();
		}
		for (const proxy of this.bproxies.values()) {
			proxy.destruct();
		}
		switch (this.collection.component_root.properties.location.state.name) {
			case "local": {
				this.forward = this;
				this.backward = this;
				for (const entry of this.collection.properties.entries.values()) {
					const node = this.getNode(entry) ?? this.createNode(entry, this, this);
					const edge = node.getEdgeValue();
					if (edge === undefined) {
						if (force_init) this._reset_as_local();
						else this._clear();
						return;
					}
					if (edge === Sink) {
						node.forward = this;
						this.backward = node;
					} else {
						const other_entry = iCollection(this.collection).find(edge);
						if (other_entry === undefined) {
							if (force_init) this._reset_as_local();
							else this._clear();
							return;
						}
						const other_node = this.getNode(other_entry) ?? this.createNode(other_entry, this, this);
						node.forward = other_node;
						other_node.backward = node;
					}
				}
				const list = new Set<string>();
				let node = this.backward;
				while (node instanceof Node) {
					const key = node.entry.key;
					if (list.has(key)) {
						if (force_init) this._reset_as_local();
						else this._clear();
						return;
					}
					list.add(key);
					if (node.backward === this) break;
					node = node.backward as Node | Graph;
				}
				this.forward = node;
				break;
			}
			case "remote":
				this.forward = this.createForwardProxy();
				this.backward = this.createBackwardProxy();
				for (const entry of this.collection.properties.entries.values()) {
					switch (entry.properties.location.state.name) {
						case "local":
							this.addEntry(entry);
							break;
						case "remote": {
							this.addServerEntry(entry);
							break;
						}
					}
				}
				break;
		}
		this.updateSorting();
	}

	sort(): string[] {
		const all_keys = [...this.collection.properties.entries.keys()];
		const list = new Set<string>();
		let node = this.forward;
		while (node instanceof Node) {
			const key = node.entry.key;
			if (list.has(key)) {
				// Loop detected; unable to sort, return unsorted keys instead
				return all_keys;
			}
			list.add(key);
			node = node.forward;
		}
		if (list.size !== all_keys.length) {
			// Incomplete assortment; return unsorted keys instead
			return all_keys;
		}
		return [...list].reverse();
	}

	_sorted: MutableVariable<string[]> | undefined;
	get sorted(): ScalarProvider<string[]> {
		if (!this._sorted || this._sorted.destroyed) {
			this._sorted = createVariable(this.sort(), true);
		}
		return this._sorted;
	}
	updateSorting() {
		this._sorted?.set(this.sort());
	}

	async queryVertex(value: string | Sink): Promise<string | Failed | None> {
		const ordering_stategroup_name = this.ordering_stategroup.parent.parent.key;
		const collection_name = this.ordered_graph.parent.parent.parent.parent.key.entry;
		const context_node_path = createIdPathBuilderFromNode(this.ordered_graph.component_root);

		const filters: Record<string, api_client_request.Tvalue_filters> = {};
		if (value === Sink) {
			filters[ordering_stategroup_name] = createNodeExistanceFilter(
				new NodePathBuilder().state(ordering_stategroup_name, this.sink_state.key).build()
			);
		} else {
			const ordering_property_path_builder = createNodePathFromObjectPathTail(this.ordering_stategroup_path);
			const edge_stategroup_path = ordering_property_path_builder.state(ordering_stategroup_name, this.edge_state.key);
			filters[ordering_stategroup_name] = createNodeExistanceFilter(edge_stategroup_path.build());
			filters[ordering_stategroup_name + this.edge_reference.parent.parent.key] = {
				path: edge_stategroup_path.reference(this.edge_reference.parent.parent.key).build(),
				type: [
					"node",
					{
						type: [
							"containment",
							{
								operator: ["in", {}],
								references: {
									"has alternative": ["no", {}],
									"referenced node": context_node_path.collectionEntry(collection_name, value).build(),
								},
							},
						],
					},
				],
			};
		}

		try {
			const result = await DatastoreProxy.instance.collectionQuery({
				filters: {
					"todo filter": ["no", {}],
					"value filters": filters,
				},
				"context node path": context_node_path.build(),
				"collection path": CollectionPathBuilder.create().collection(collection_name).build(),
				"select entries": [
					"yes",
					{
						"limit number of entries": 1,
						properties: {
							properties: {},
						},
					},
				],
				aggregates: {},
			});
			switch (result.properties.context.state.name) {
				case "exists": {
					const exists = result.properties.context.state.node;
					switch (exists.properties.entries.state.name) {
						case "yes": {
							const $ = exists.properties.entries.state.node;
							switch ($.properties.limit_exceeded.state.name) {
								case "no":
									switch ($.properties.entries.properties.has_steps.state.name) {
										case "yes": {
											const collection = $.properties.entries.properties.has_steps.state.node;
											const [entry] = [...collection.properties.entries.keys()];
											return entry;
										}
										case "no":
											return Failed;
									}
									break;
								case "yes":
									return Failed;
							}
							break;
						}
						case "no":
							return None;
					}
					break;
				}
				case "does not exist":
					return Failed;
				case "permission denied":
					return Failed;
			}
		} catch (e) {
			return Failed;
		}
	}

	setServerEdgeValue(entry: Entry | undefined, value: string | Sink) {
		let node;
		if (entry) {
			node = this.nodes.get(entry);
			if (!node) {
				return this.addServerEntry(entry);
			}
			this.getForwardProxy(entry)?.destruct();
		} else {
			// eslint-disable-next-line @typescript-eslint/no-this-alias
			node = this;
		}
		if (node.isModified()) {
			return this.reset();
		}
		const other_node = this.findByKey(value);
		if (other_node) {
			if (other_node.backward.isModified()) {
				return this.reset();
			}
			if (other_node.backward instanceof BackwardProxy) {
				other_node.backward.destruct();
			}
			node.forward = other_node;
			other_node.backward = node;
		} else {
			const forward = node.forward;
			if (forward instanceof ForwardProxy) {
				forward.destruct();
			} else {
				forward.backward = forward.createBackwardProxy();
			}
			node.forward = node.createForwardProxy();
		}
		this.updateSorting();
	}

	setServerSourceValue(value: string | Sink) {
		if (this.source_key === value) return;
		if (this.source_key !== Pending) {
			this.source_key = value;
			return this.setServerEdgeValue(undefined, value);
		}
		this.source_key = value;

		if (this.forward instanceof Node) {
			if (this.forward.key !== value) {
				this.reset();
			}
			return;
		}
		if (this.forward === this) {
			this.forward = this.createForwardProxy();
			this.backward = this.createBackwardProxy();
		}
		const fproxy = this.getForwardProxy(this);
		if (fproxy) {
			fproxy.key = value;
			fproxy.backward.updateEdge("user");
		}
		for (const bproxy of this.bproxies.values()) {
			if (bproxy.edge === value) {
				if (fproxy) {
					this.merge(bproxy, fproxy);
				}
				break;
			}
		}
		this.updateSorting();
	}

	updateServerKeyValue(entry: Entry, value: string) {
		const proxy = this.getBackwardProxy(entry);
		if (proxy) {
			proxy.edge = value;
		}
	}

	addServerEntry(entry: Entry): Node | undefined {
		let node = this.getNode(entry);
		if (node) return node;

		node = this.createNode(entry);
		const edge = node.getServerEdgeValue();
		if (edge === undefined) {
			(node.forward as ForwardProxy).destruct();
			(node.backward as BackwardProxy).destruct();
			node.destruct();
			return undefined;
		}

		const key_value = getBackendKeyValue(entry);
		for (const proxy of this.fproxies.values()) {
			if (proxy.key === key_value) {
				const backward = proxy.backward;
				(node.backward as BackwardProxy).destruct();
				node.backward = backward;
				backward.forward = node;
				proxy.destruct();
				break;
			}
		}
		for (const proxy of this.bproxies.values()) {
			if (proxy.edge === edge) {
				(node.forward as ForwardProxy).destruct();
				this.connect(node, proxy.forward);
				proxy.destruct();
				break;
			}
		}
		this.updateSorting();
		return node;
	}

	deleteServerEntry(entry: Entry) {
		const node = this.getNode(entry);
		if (!node) return;
		this.connect(node.backward, node.forward);
		node.destruct();
	}

	removeEntry(entry: Entry) {
		const node = this.getNode(entry);
		if (!node) return;
		switch (entry.properties.location.state.name) {
			case "remote": {
				if (!Entry.isMarkedForDeletion(node.entry) && node.edgeModified()) {
					this.reset();
					return;
				}
				const backward = node.backward;
				const forward = node.forward;
				if (backward instanceof BackwardProxy) {
					backward.destruct();
				} else {
					backward.forward = backward.createForwardProxy();
				}
				if (forward instanceof ForwardProxy) {
					forward.destruct();
				} else {
					forward.backward = forward.createBackwardProxy();
				}
				node.destruct();
				this.updateSorting();
				break;
			}
			case "local":
				this.connect(node.backward, node.forward);
				node.destruct();
				this.updateSorting();
				break;
		}
	}

	addEntry(entry: Entry, by: "user" | "engine" = "user") {
		switch (this.default) {
			case Sink: {
				const node = this.createNode(entry, this.backward, this);
				const backward = this.backward;
				this.connect(backward, node, by);
				this.connect(node, this, by);
				break;
			}
			case Source: {
				const node = this.createNode(entry, this, this.forward);
				const forward = this.forward;
				this.connect(this, node, by);
				this.connect(node, forward, by);
				break;
			}
		}
		this.updateSorting();
	}

	deleteEntry(entry: Entry) {
		const node = this.getNode(entry);
		if (!node) return;
		switch (entry.properties.location.state.name) {
			case "remote":
				node.backward.updateEdge("user");
				break;
			case "local":
				this.connect(node.backward, node.forward);
				node.destruct();
				this.updateSorting();
				break;
		}
	}

	insertBeforeKey(entry: Entry, key: string) {
		const other_entry = iCollection(this.collection).find(key);
		if (other_entry) {
			this.insertBefore(entry, other_entry);
		} else {
			const node = this.getNode(entry);
			if (!node) return;
			const fproxy = new ForwardProxy(this, undefined, this, key);
			const bproxy = new BackwardProxy(this, undefined, fproxy, key);
			fproxy.backward = bproxy;
			this.move(node, fproxy);
		}
	}

	insertBefore(entry_to_insert: Entry, before_entry: Entry) {
		const node_to_insert = this.getNode(entry_to_insert);
		const before_node = this.getNode(before_entry);
		this.move(node_to_insert, before_node);
	}

	insertAfter(entry_to_insert: Entry, after_entry: Entry) {
		const node_to_insert = this.getNode(entry_to_insert);
		const after_node = this.getNode(after_entry);
		this.move(node_to_insert, after_node?.forward);
	}

	moveForward(entry: Entry) {
		const node = this.getNode(entry);
		const forward = node?.forward;
		if (!(forward instanceof Node)) return;
		this.move(node, forward.forward);
	}

	moveBackward(entry: Entry) {
		const node = this.getNode(entry);
		const backward = node?.backward;
		if (!(backward instanceof Node)) return;
		this.move(node, backward);
	}

	moveToSink(entry: Entry) {
		const node = this.getNode(entry);
		this.move(node, this);
	}

	moveToSource(entry: Entry) {
		const node = this.getNode(entry);
		this.move(node, this.forward);
	}
}

export type iGraph = Graph;
export const iGraph = WeakCache<api_cs.Cordered_graphs, Graph>((graph) => new Graph(graph));
